// format.ts — small display-formatting helpers shared across every screen that renders raw
// server numbers (bytes, timestamps). Extracted the moment a second screen (Usage AND
// Backups both render byte counts) needed the identical formatting.

export function formatBytes(bytes: number | undefined | null): string {
  if (bytes === undefined || bytes === null) return "—";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.min(Math.floor(Math.log(Math.abs(bytes)) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

// THE DISPLAY ZONE (A5 / P9). Servers, databases and logs are UTC; a console shows times in the zone
// its session reports (/auth/session time_zone: the login's override, else its account's zone, else
// UTC) and gives UTC on hover (<When>). Set once by AuthProvider; UTC until then, never the browser's
// own zone silently — the same instant must read the same on every operator's screen.
let displayZone = "UTC";

/** Sets the zone formatDateTime shows; an unknown or empty name falls back to UTC. */
export function setDisplayTimeZone(zone: string | undefined | null): void {
  const z = (zone ?? "").trim();
  if (!z) {
    displayZone = "UTC";
    return;
  }
  try {
    new Intl.DateTimeFormat("en", { timeZone: z });
    displayZone = z;
  } catch {
    displayZone = "UTC";
  }
}

/** The zone times are shown in now. */
export function displayTimeZone(): string {
  return displayZone;
}

function toDate(value: string | number | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

/** A time in the display zone, with the zone named. */
export function formatDateTime(value: string | number | Date | undefined | null): string {
  if (value === undefined || value === null || value === "") return "—";
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, { timeZone: displayZone, timeZoneName: "short" });
}

/** The same instant in UTC, ISO-like — what <When> shows on hover. */
export function formatUTC(value: string | number | Date | undefined | null): string {
  if (value === undefined || value === null || value === "") return "—";
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toISOString().replace("T", " ").replace(/\.\d+Z$/, " UTC");
}

export function formatRelative(value: string | undefined | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const diffMs = Date.now() - d.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return "just now";
  if (abs < 3600) return `${Math.round(abs / 60)}m ago`;
  if (abs < 86400) return `${Math.round(abs / 3600)}h ago`;
  return `${Math.round(abs / 86400)}d ago`;
}

/** A percentage that may arrive as a number or as text already carrying "%" (podman stats sends
 *  "0.00%"); appending another gave "0.00%%". */
export function formatPercent(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === "") return "—";
  return `${String(value).trim().replace(/%$/, "")}%`;
}
